import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api/axios";
import { v4 as uuidv4 } from "uuid";
import { useCurrentTenant } from "../../../hooks/useCurrentTenant";
import "./EnclosureSelectorEnhanced.css";

const DIMS = [
  ["width", "Width"],
  ["height", "Height"],
  ["depth", "Depth"],
];
const emptyFilters = { width: {}, height: {}, depth: {}, manufacturers: [] };

const mfrOf = (e) => e.manufacturer || "Unnamed";
const sizeOf = (e, k) => Number(e.size?.[k] ?? 0);

const chipsFrom = (f) => {
  const chips = [];
  DIMS.forEach(([k, label]) => {
    if (f[k].min !== undefined)
      chips.push({ key: `${k}-min`, text: `${label}: >=${f[k].min}mm`, clear: () => ({ ...f, [k]: { ...f[k], min: undefined } }) });
    if (f[k].max !== undefined)
      chips.push({ key: `${k}-max`, text: `${label}: <=${f[k].max}mm`, clear: () => ({ ...f, [k]: { ...f[k], max: undefined } }) });
  });
  f.manufacturers.forEach((m) =>
    chips.push({ key: `m-${m}`, text: m, clear: () => ({ ...f, manufacturers: f.manufacturers.filter((x) => x !== m) }) })
  );
  return chips;
};

function EnclosureCard({ enc, checked, disabled, onSelect }) {
  const label = `${mfrOf(enc)} ${enc.name ?? ""} ${enc.articleNumber ?? ""}`.trim();
  return (
    <label className={`es-card ${checked ? "selected" : ""} ${disabled ? "disabled" : ""}`}>
      {checked && <span className="es-flag">Selected</span>}
      <input
        className="es-check"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => { if (!checked) onSelect(enc.id); }}
        aria-label={`${checked ? "Selected: " : "Select "}${label}`}
      />
      <span className="es-logo">
        {enc.logoUrl ? <img src={enc.logoUrl} alt="" /> : mfrOf(enc).slice(0, 2).toUpperCase()}
      </span>
      <span className="es-info">
        <strong>{enc.name || enc.articleNumber || "Unnamed enclosure"}</strong>
        {enc.articleNumber && enc.name ? <small>{enc.articleNumber}</small> : null}
        <small>WHD: {sizeOf(enc, "width")} x {sizeOf(enc, "height")} x {sizeOf(enc, "depth")} mm</small>
      </span>
    </label>
  );
}

export default function EnclosureSelectorEnhanced({
  selectedProject,
  onUpdateProject,
  requiredHeight,
  enclosureTooSmall,
  onClearEnclosureBlock,
  onEnclosureChanged,
}) {
  const currentTenantId = useCurrentTenant();
  const projectId = selectedProject?.id;

  const [masterList, setMasterList] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(selectedProject?.enclosurePlacement?.referenceId ?? "");
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  // 1) Load enclosure master data
  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    api.get("/enclosure")
      .then((res) => { if (!cancelled) { setMasterList(res.data || []); setLoadError(false); } })
      .catch((err) => { console.error("Failed to load enclosures:", err); if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setListLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const tenantEnclosures = useMemo(
    () => masterList.filter((e) => e.tenantId === currentTenantId && e.ownership === "Tenant"),
    [masterList, currentTenantId]
  );

  // Keep UI in sync after project changes
  useEffect(() => {
    setSelectedId(selectedProject?.enclosurePlacement?.referenceId ?? "");
  }, [selectedProject?.id, selectedProject?.enclosurePlacement]);

  // Selected enclosure (looked up in the whole master list so it never vanishes)
  const selected = useMemo(
    () => (selectedId ? masterList.find((e) => e.id === selectedId) ?? null : null),
    [masterList, selectedId]
  );

  const manufacturers = useMemo(() => [...new Set(tenantEnclosures.map(mfrOf))].sort(), [tenantEnclosures]);

  // Everything except the selected one, narrowed by the "too small" guard, search and filters
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenantEnclosures.filter((e) => {
      if (e.id === selectedId) return false;
      if (requiredHeight && enclosureTooSmall && sizeOf(e, "height") < requiredHeight) return false;
      if (q) {
        const hay = `${mfrOf(e)} ${e.articleNumber ?? ""} ${e.name ?? ""} ${sizeOf(e, "width")}x${sizeOf(e, "height")}x${sizeOf(e, "depth")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.manufacturers.length && !filters.manufacturers.includes(mfrOf(e))) return false;
      return DIMS.every(([k]) => {
        const { min, max } = filters[k];
        const v = sizeOf(e, k);
        return (min === undefined || v >= min) && (max === undefined || v <= max);
      });
    });
  }, [tenantEnclosures, selectedId, query, filters, requiredHeight, enclosureTooSmall]);

  const groups = useMemo(() => {
    const m = new Map();
    visible.forEach((e) => m.set(mfrOf(e), [...(m.get(mfrOf(e)) || []), e]));
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [visible]);

  // 2) Selection → create/update EnclosurePlacement (unchanged logic)
  const handleSelect = async (encId) => {
    setSelectedId(encId);
    if (!projectId || !encId) return;
    const model = tenantEnclosures.find((m) => m.id === encId);
    if (!model) return;

    setLoading(true);
    try {
      const existing = selectedProject.enclosurePlacement;
      const enclosurePosition = existing?.position ?? { x: 0, y: 0, z: 0 };
      const enclosureWidth = Number(model?.size?.width ?? 0);
      const enclosureHeight = Number(model?.size?.height ?? 0);
      const montageWidth = Number(model?.montageplate?.size?.width ?? 0);
      const montageHeight = Number(model?.montageplate?.size?.height ?? 0);

      const montagePlacement = model?.montageplate
        ? {
            id: existing?.montagePlacement?.id ?? uuidv4(),
            referenceId: model.id,
            parentId: existing?.id ?? uuidv4(),
            size: {
              width: montageWidth,
              height: montageHeight,
              depth: model.montageplate.size?.depth ?? 0,
            },
            position: {
              x: enclosurePosition.x + (enclosureWidth - montageWidth) / 2,
              y: enclosurePosition.y + (enclosureHeight - montageHeight) / 2,
              z: 0,
            },
            mountingDistance: model.montageplate.mountingDistance,
          }
        : null;

      const payload = {
        id: existing?.id ?? uuidv4(),
        parentId: projectId,
        referenceId: model.id,
        position: { x: 0, y: 0 },
        size: model.size,
        // reset enclosure-dependent data
        ductLeft: null,
        ductRight: null,
        ductTop: null,
        montagePlacement,
        sectionPlacements: existing?.sectionPlacements ?? [],
        devices: existing?.devices ?? [],
      };

      await api.put(`/project/${projectId}/enclosure`, payload);
      const updatedProject = await api.get(`/project/${projectId}`).then((r) => r.data);

      onClearEnclosureBlock?.();
      onUpdateProject?.(updatedProject);
      onEnclosureChanged?.();
    } catch (err) {
      console.error("Failed to persist enclosure:", err);
      setSelectedId(selectedProject?.enclosurePlacement?.referenceId ?? "");
    } finally {
      setLoading(false);
    }
  };

  const chips = chipsFrom(filters);
  const setRange = (k, bound) => (ev) => {
    const v = ev.target.value === "" ? undefined : Number(ev.target.value);
    setFilters((f) => ({ ...f, [k]: { ...f[k], [bound]: v } }));
  };
  const toggleManufacturer = (m) =>
    setFilters((f) => ({
      ...f,
      manufacturers: f.manufacturers.includes(m) ? f.manufacturers.filter((x) => x !== m) : [...f.manufacturers, m],
    }));

  const othersTotal = tenantEnclosures.length - (selected && tenantEnclosures.some((e) => e.id === selected.id) ? 1 : 0);

  return (
    <div className="es-root">
      {/* Fixed header: search, filters, selected enclosure. Does not scroll. */}
      <div className="es-header">
        <input
          className="pl-input"
          type="search"
          placeholder="Search"
          aria-label="Search enclosures"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <button type="button" className="es-link" aria-expanded={showFilters} onClick={() => setShowFilters((s) => !s)}>
          Add enclosure requirements / size constraints
        </button>

        {showFilters && (
          <div className="es-filters">
            {DIMS.map(([k, label]) => (
              <div className="es-filter-row" key={k}>
                <span>{label} (mm)</span>
                <input className="pl-input" type="number" min="0" placeholder="min" value={filters[k].min ?? ""} onChange={setRange(k, "min")} />
                <input className="pl-input" type="number" min="0" placeholder="max" value={filters[k].max ?? ""} onChange={setRange(k, "max")} />
              </div>
            ))}
            <div className="es-filter-mfr">
              <span>Manufacturer</span>
              <div className="es-mfr-list">
                {manufacturers.map((m) => (
                  <button
                    type="button"
                    key={m}
                    className={`es-toggle ${filters.manufacturers.includes(m) ? "on" : ""}`}
                    aria-pressed={filters.manufacturers.includes(m)}
                    onClick={() => toggleManufacturer(m)}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {chips.length > 0 && (
          <>
            <div className="es-chips">
              {chips.map((c) => (
                <span className="es-chip" key={c.key}>
                  {c.text}
                  <button type="button" aria-label={`Remove ${c.text}`} onClick={() => setFilters(c.clear())}>×</button>
                </span>
              ))}
            </div>
            <button type="button" className="es-link es-clear" onClick={() => setFilters(emptyFilters)}>Clear all</button>
          </>
        )}

        {enclosureTooSmall && requiredHeight ? (
          <div className="pl-alert" role="alert">
            <strong>Enclosure too small</strong>
            <p>Choose a larger enclosure (≥ {requiredHeight} mm). Smaller ones are hidden.</p>
          </div>
        ) : null}

        {loading && <span className="enclosure-loading">Updating…</span>}

        {/* Selected enclosure, pinned */}
        {selected && (
          <>
            <div className="es-section-title">Selected enclosure</div>
            <EnclosureCard enc={selected} checked disabled={loading} onSelect={handleSelect} />
            <hr className="es-divider" />
          </>
        )}

        {!listLoading && !loadError && (
          <div className="es-count">
            {visible.length} of {othersTotal} {selected ? "other " : ""}enclosure{othersTotal === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {/* Scrolling list */}
      <div className="es-list">
        {listLoading && <p className="pl-empty">Loading enclosures…</p>}
        {loadError && <p className="pl-empty">Failed to load enclosures.</p>}

        {!listLoading && !loadError && groups.length === 0 && (
          <p className="pl-empty">
            {tenantEnclosures.length === 0
              ? `No enclosures found for this tenant (${masterList.length} loaded in total).`
              : "No enclosures match your search or filters."}
          </p>
        )}

        {groups.map(([mfr, items]) => {
          const open = !collapsed[mfr];
          return (
            <section className="es-group" key={mfr}>
              <button
                type="button"
                className="es-group-head"
                aria-expanded={open}
                onClick={() => setCollapsed((c) => ({ ...c, [mfr]: open }))}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                  style={{ transform: open ? "none" : "rotate(-90deg)" }} aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
                {mfr} ({items.length})
              </button>
              {open && items.map((e) => (
                <EnclosureCard key={e.id} enc={e} checked={false} disabled={loading} onSelect={handleSelect} />
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}